/**
 * @file Loads /config.json before the application renders (F2.4).
 *
 * One build serves every environment: the container writes config.json from
 * environment variables at start and the app reads it here, before anything
 * else runs. A missing or invalid file is a deployment error and surfaces as a
 * ConfigLoadError so the bootstrap can show an honest failure state instead of
 * a half-working store.
 */
import { runtimeConfigSchema } from './schema';

import type { RuntimeConfig } from './schema';

/** Thrown when config.json cannot be fetched or fails validation. */
export class ConfigLoadError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'ConfigLoadError';
  }
}

/** Where the runtime configuration is served from. */
export const CONFIG_URL = '/config.json';

/** Fetches and validates the runtime configuration. `fetchImpl` is injectable for tests. */
export async function loadRuntimeConfig(
  fetchImpl: typeof fetch = fetch,
  url: string = CONFIG_URL,
): Promise<RuntimeConfig> {
  let response: Response;
  try {
    response = await fetchImpl(url, { cache: 'no-store', credentials: 'same-origin' });
  } catch (error) {
    throw new ConfigLoadError('Runtime configuration could not be fetched', { cause: error });
  }
  if (!response.ok) {
    throw new ConfigLoadError(
      `Runtime configuration responded with status ${String(response.status)}`,
    );
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch (error) {
    throw new ConfigLoadError('Runtime configuration is not valid JSON', { cause: error });
  }
  const parsed = runtimeConfigSchema.safeParse(payload);
  if (!parsed.success) {
    const fields = parsed.error.issues.map((issue) => issue.path.map(String).join('.')).join(', ');
    throw new ConfigLoadError(`Runtime configuration is invalid: ${fields}`);
  }
  return parsed.data;
}
